import { type ReactNode, useId } from 'react'
import { Button } from '@/components/Button'
import { Form } from '@/components/Form'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentIllustration,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from '@/components/ModalSheet'
import CreateTagIllustration from '@/components/SpotIllustration/CreateTagIllustration'

export interface NewLegendDialogProps {
  showing: boolean
  corporateMode: boolean
  isPending: boolean
  canCreate: boolean
  onClose: () => unknown
  onSubmit: () => unknown
  children?: ReactNode
  testId?: string
}

/**
 * Asks for the name of a new legend.
 */
export function NewLegendDialog({
  showing,
  corporateMode,
  isPending,
  canCreate,
  onClose,
  onSubmit,
  children,
  testId,
}: NewLegendDialogProps) {
  const formId = useId()
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isPending ? undefined : onClose}
      testId={testId}
    >
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <ModalSheetContentBlock>
          {!corporateMode && (
            <ModalSheetContentIllustration
              illustration={CreateTagIllustration}
              alt="Hand placing a tag"
            />
          )}
          <ModalSheetContentTitle>New legend</ModalSheetContentTitle>
          <ModalSheetContentDescription>
            Enter an appropriate name for the new legend below.
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
        <ModalSheetContentBlock>
          <Form id={formId} onSubmit={() => onSubmit()}>
            {children}
          </Form>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Cancel"
            isDisabled={isPending}
            onPress={onClose}
            variant="default"
          />
          <Button
            label="Create"
            type="submit"
            form={formId}
            isPending={isPending}
            isDisabled={isPending || !canCreate}
            variant="primary"
            testId={testId && `${testId}-create`}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
